/**
 * Autenticación contra Cognito vía SRP (sin exponer el secreto del cliente).
 * Copia deliberada de `frontend/src/auth.ts` (doc 03 Fase 3: pool del
 * operador de plataforma, separado del de organismos) — la lógica es
 * genérica (no depende de org_id/roles), así que no hay nada que adaptar
 * salvo apuntar `config.ts` al pool del operador. MFA (TOTP) es obligatorio
 * (mismo criterio que el pool de organismos) — este módulo maneja tanto el
 * primer enrolamiento (challenge MFA_SETUP: se muestra un secreto para
 * cargar en una app autenticadora) como la verificación en logins
 * posteriores (challenge SOFTWARE_TOKEN_MFA).
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from './config.js';

function pool(): CognitoUserPool {
  return new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.clientId });
}

const EMISOR_TOTP = 'Sumarios Operador';

/**
 * URI estándar `otpauth://` (RFC de Google Authenticator) para que la app
 * autenticadora se configure escaneando un QR en vez de tipear el secreto a
 * mano. Se arma en el cliente a partir del secreto que ya devolvió Cognito
 * — nunca se envía a un servicio externo de generación de QR (el secreto es
 * sensible; el QR se dibuja localmente, ver App.tsx con la librería `qrcode`).
 */
function otpauthUri(secreto: string, email: string): string {
  const etiqueta = `${EMISOR_TOTP}:${email}`;
  return `otpauth://totp/${encodeURIComponent(etiqueta)}?secret=${secreto}&issuer=${encodeURIComponent(EMISOR_TOTP)}`;
}

export interface Sesion {
  idToken: string;
  accessToken: string;
  email: string;
}

/**
 * Resultado de intentar iniciar sesión: éxito directo, o un desafío pendiente
 * de completar. `nueva_clave` es el primer login tras un alta (Cognito exige
 * reemplazar la clave temporal, doc 03 Fase 3 — `crear-organismo.ts` siempre
 * crea el ADMIN_ORG en este estado). `completar` devuelve otro
 * `ResultadoLogin`, no una `Sesion` directa, porque tras poner la clave nueva
 * Cognito encadena el desafío MFA_SETUP (mismo patrón de `mfa_configurar`).
 */
export type ResultadoLogin =
  | { tipo: 'ok'; sesion: Sesion }
  | { tipo: 'nueva_clave'; completar: (claveNueva: string) => Promise<ResultadoLogin> }
  | { tipo: 'mfa_verificar'; verificar: (codigo: string) => Promise<Sesion> }
  | {
      tipo: 'mfa_configurar';
      secreto: string;
      otpauthUri: string;
      verificar: (codigo: string) => Promise<Sesion>;
    };

function sesionDeCognito(session: CognitoUserSession, email: string): Sesion {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    email,
  };
}

/**
 * Callbacks de Cognito compartidos entre el login inicial
 * (`authenticateUser`) y la continuación tras poner la clave nueva
 * (`completeNewPasswordChallenge`) — ambos pueden desembocar en los mismos
 * desafíos (MFA_SETUP, SOFTWARE_TOKEN_MFA) o en éxito directo.
 */
function desafiosLogin(
  user: CognitoUser,
  email: string,
  resolve: (r: ResultadoLogin) => void,
  reject: (e: unknown) => void,
) {
  return {
    onSuccess: (session: CognitoUserSession) =>
      resolve({ tipo: 'ok', sesion: sesionDeCognito(session, email) }),
    onFailure: (err: unknown) => reject(err),
    newPasswordRequired: () => {
      resolve({
        tipo: 'nueva_clave',
        completar: (claveNueva) =>
          new Promise((res, rej) => {
            user.completeNewPasswordChallenge(claveNueva, {}, desafiosLogin(user, email, res, rej));
          }),
      });
    },
    // Primer login del usuario: aún no tiene TOTP asociado.
    mfaSetup: () => {
      user.associateSoftwareToken({
        associateSecretCode: (secreto: string) => {
          resolve({
            tipo: 'mfa_configurar',
            secreto,
            otpauthUri: otpauthUri(secreto, email),
            verificar: (codigo) =>
              new Promise((res, rej) => {
                user.verifySoftwareToken(codigo, 'principal', {
                  onSuccess: (session) => res(sesionDeCognito(session, email)),
                  onFailure: (e) => rej(e),
                });
              }),
          });
        },
        onFailure: (err: unknown) => reject(err),
      });
    },
    // Logins posteriores: ya tiene TOTP asociado, solo pide el código.
    totpRequired: () => {
      resolve({
        tipo: 'mfa_verificar',
        verificar: (codigo) =>
          new Promise((res, rej) => {
            user.sendMFACode(
              codigo,
              {
                onSuccess: (session) => res(sesionDeCognito(session, email)),
                onFailure: (e) => rej(e),
              },
              'SOFTWARE_TOKEN_MFA',
            );
          }),
      });
    },
  };
}

export function iniciarSesion(email: string, password: string): Promise<ResultadoLogin> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, desafiosLogin(user, email, resolve, reject));
  });
}

export function cerrarSesion(): void {
  pool().getCurrentUser()?.signOut();
}

export function sesionActual(): Promise<Sesion | null> {
  const user = pool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        email: user.getUsername(),
      });
    });
  });
}
